import { useAuth } from 'react-oidc-context';
import LoadingButton from './LoadingButton';
import { useContext, useEffect, useState } from 'react';
import { CartContext } from '../context/cartContext';
import {
  AddToCartButtonProps,
  OutOfStock,
  Product,
} from '../interface/interface';
import { calculateOutOfStockEnum } from '../utils/outOfStockStatus';
import showToast from './ToastComponent';
import ProductQuantityIncrement from './ProductQuantityIncrement';
import { AlertIcon } from '../assets/svgImages';
import { AllowedProducts } from './AllowedProducts';
import CustomModal from './CustomModal';
import ProductCards from './ProductCards';
import { UserContext } from '../context/userContext';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from './Tooltip';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getCart } from '../service/getCart';
import { addProductToCart } from '../service/addProductToCart';
import { removeProductFromCart } from '../service/removeProductFromCart';

const AddToCartButton = ({
  product,
  maxQty,
  substituteProduct,
}: AddToCartButtonProps) => {
  const { user } = useAuth();
  const [products, setProducts] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [openSubstituteModal, setSubstituteModal] = useState(false);
  const [suggestAccessoriesModal, setSuggestAccessories] = useState(false);
  const [addProductModalLoading, setAddProductModalLoading] = useState(false);
  const { userDetail, iseRxDown } = useContext(UserContext);
  const [showInvalidMailTooltip, setShowInvalidMailTooltip] = useState(false);
  const navigate = useNavigate();
  const { setLoadingEnable } = useContext(UserContext);
  const { cart, setCart } = useContext(CartContext);
  const qc = useQueryClient();

  const { isLoading } = useQuery({
    queryKey: ['cart', user?.access_token],
    queryFn: async () => {
      const result = await getCart(user!.access_token);
      if (result === undefined) {
        showToast({
          message: 'Unable to fetch existing cart!',
          type: 'error',
        });
        return null;
      }

      setCart(result);
      return result;
    },
    enabled: !!user?.access_token,
  });

  const addToCartMutation = useMutation({
    mutationFn: async (productId: string) => {
      const result = await addProductToCart(user!.access_token, productId);
      if (result?.status === 200) {
        showToast({
          message: 'Item added to your cart.',
          type: 'success',
        });
        qc.invalidateQueries({
          queryKey: ['cart', user?.access_token],
        });
      }

      if (result?.status === 405) {
        showToast({
          message: result.data.message,
          type: 'warning',
        });
      }
      setSubstituteModal(false);
      return null;
    },
    mutationKey: ['addToCart', user?.access_token],
  });

  const removeProductFromCartMutation = useMutation({
    mutationKey: ['removeCartItem', user?.access_token],
    mutationFn: async (productId: string) => {
      const result = await removeProductFromCart(user!.access_token, productId);
      if (result?.status === 200) {
        showToast({
          message: 'Item removed from your cart.',
          type: 'success',
        });
        qc.invalidateQueries({
          queryKey: ['cart', user?.access_token],
        });
      }
      if (result?.status === 405) {
        showToast({
          message: result.data.message,
          type: 'info',
        });
      }
      setSubstituteModal(false);
      return null;
    },
  });

  useEffect(() => {
    if (
      !isLoading ||
      !addToCartMutation.isPending ||
      !removeProductFromCartMutation.isPending
    ) {
      setLoadingEnable(false);
    } else {
      setLoadingEnable(true);
    }
  }, [
    isLoading,
    addToCartMutation.isPending,
    removeProductFromCartMutation.isPending,
  ]);

  useEffect(() => {
    setProducts(frameProductObject(product));
  }, [cart, product?.product_code]);

  function frameProductObject(product: Product) {
    const orderedQuantity =
      cart?.line_items?.find(
        (item: any) => `${item?.product_id}` === `${product.product_code}`,
      )?.quantity ?? 0;

    if (substituteProduct) {
      const subProduct = substituteProduct;
      const subOrderedQuantity =
        cart?.line_items?.find(
          (item: any) => `${item?.product_id}` === `${subProduct.product_code}`,
        )?.quantity ?? 0;

      return {
        ...product,
        substituteProduct: {
          ...subProduct,
          productMaxQty: product?.productMaxQty ?? maxQty,
          orderedQuantity: subOrderedQuantity,
          outOfStock: calculateOutOfStockEnum(subProduct, orderedQuantity),
        },
        productMaxQty: product?.productMaxQty ?? maxQty,
        orderedQuantity,
        outOfStock: calculateOutOfStockEnum(product, orderedQuantity),
      };
    }

    return {
      ...product,
      orderedQuantity,
      outOfStock: calculateOutOfStockEnum(product, orderedQuantity),
    };
  }

  async function handleAddToCartClick() {
    setLoading(true);
    await addToCartMutation
      .mutateAsync(product.product_code)
      .finally(() => setLoading(false));
  }

  async function handleProceed() {
    setAddProductModalLoading(true);
    await addToCartMutation.mutateAsync(product.product_code).then(() => {
      setAddProductModalLoading(false);
      navigate('/non-script-range');
    });
  }

  async function handleStayOnPage() {
    setSuggestAccessories(false);
    setLoading(true);
    await addToCartMutation
      .mutateAsync(product.product_code)
      .finally(() => setLoading(false));
  }

  return (
    <div className="sm:my-2 sm:mx-4 md:m-0 md:block">
      {products?.orderedQuantity > 0 ? (
        <ProductQuantityIncrement
          productCode={product.product_code}
          orderedQuantity={products.orderedQuantity}
          productOutOfStock={products.outOfStock}
          maxQuantity={products.productMaxQty ?? maxQty}
          addProductToCart={async (id: string) => {
            await addToCartMutation.mutateAsync(id);
          }}
          removeProductFromCart={async (productId: string) => {
            await removeProductFromCartMutation.mutateAsync(productId);
          }}
        />
      ) : substituteProduct ? (
        <></>
      ) : userDetail?.supportingParams?.hasValidEmail ? (
        <>
          <LoadingButton
            buttonText="Add to Cart"
            isLoading={loading}
            buttonIcon={<span>🛒</span>}
            isDisabled={
              (products?.outOfStock &&
                products.outOfStock !== OutOfStock.false) ||
              !userDetail?.supportingParams?.isUserAllowedToOrder ||
              iseRxDown
            }
            onClick={handleAddToCartClick}
          />
          {products?.allowedProduct && products?.allowedProducts && (
            <AllowedProducts
              product={products}
              addProductToCart={async (id: string) => {
                await addToCartMutation.mutateAsync(id);
              }}
              removeProductFromCart={async (productId: string) => {
                await removeProductFromCartMutation.mutateAsync(productId);
              }}
            />
          )}
        </>
      ) : (
        <div className="relative">
          <button
            className="bg-primary-red-400 text-white text-sm rounded p-3 flex items-center mx-auto w-full justify-center cursor-not-allowed"
            onMouseEnter={() => setShowInvalidMailTooltip(true)}
            onMouseLeave={() => setShowInvalidMailTooltip(false)}
          >
            <AlertIcon className="fill-white" />
            <span className="ml-1.5">Ordering Temporarily Unavailable</span>
          </button>
          {showInvalidMailTooltip && (
            <Tooltip
              text="You cannot place orders as your email is invalid. Please update your email address in your profile. 
                Note, this will also change your login username."
              className="z-50 top-14 !w-full xl:!w-[350px]"
            />
          )}
        </div>
      )}
      {substituteProduct &&
        !product.substitutions_not_available &&
        userDetail?.supportingParams?.hasValidEmail && (
          <div className="">
            <button
              className="w-full bg-secondary-cornflowerblue-600 border border-secondary-cornflowerblue-600 py-3 px-2
              rounded-md text-white text-sm font-semibold 
              hover:text-secondary-cornflowerblue-600 hover:bg-white disabled:bg-slate-400 disabled:border-slate-400 
              disabled:text-white disabled:cursor-not-allowed"
              onClick={() => setSubstituteModal(true)}
              disabled={!userDetail?.supportingParams?.isUserAllowedToOrder}
            >
              Order substitution
            </button>
            <CustomModal
              isOpen={openSubstituteModal}
              onClose={() => setSubstituteModal(false)}
              header=""
              footer=""
              modalSize="w-11/12 sm-land:w-3/4 md:w-3/5 lg:w-2/5 2xl:w-1/4 3xl:w-1/5"
            >
              <div>
                <p className="mb-3 text-sm font-regular text-primary-blue-500 text-left">
                  Substitute product for:{' '}
                  <span className="font-semibold">{product.name}</span>
                </p>
                <ProductCards
                  product={products?.substituteProduct}
                  addProductToCart={async (id: string) => {
                    await addToCartMutation.mutateAsync(id);
                  }}
                  removeProductFromCart={async (productId: string) => {
                    await removeProductFromCartMutation.mutateAsync(productId);
                  }}
                  substitueCard={true}
                  isAccessories={false}
                  allowedProduct={false}
                />
              </div>
            </CustomModal>
          </div>
        )}
      <div className="absolute text-start bottom-4 left-0 w-full z-40">
        <CustomModal
          isOpen={suggestAccessoriesModal}
          onClose={handleStayOnPage}
          header="Dried Herb Product Added!"
          footer={
            <>
              <LoadingButton
                isLoading={addProductModalLoading}
                className="!text-base !px-6 !py-3 !w-auto !rounded-md !mr-2"
                onClick={handleProceed}
                buttonText="Proceed"
              />
              <button
                className="text-base px-6 py-3 rounded-md !border !border-primary-blue-500"
                onClick={handleStayOnPage}
              >
                Stay on this page
              </button>
            </>
          }
          modalSize="w-11/12 sm-land:w-3/4 md:w-3/5 lg:w-2/5 2xl:w-1/4"
        >
          <div className="text-start">
            Would you like to purchase required accessories along with this?
          </div>
        </CustomModal>
      </div>
    </div>
  );
};

export default AddToCartButton;
